import type { KeycloakTokenParsed } from 'keycloak-js';
import type { LiteralUnion } from 'type-fest';

import type { AbapLintRuleMeta } from '@/abap/types';
import type {
  TLAnalyticsCalculation,
  TLConversationType,
  TLNodeInput,
  TLSendMessageRequest,
  TLSendMessageRequestContext,
} from '@/api/gen';
import type {
  TLCodeRemediationV3ActivationMessage,
  TLDashboardType,
  TLPreviewCapabilities,
  TLSAPVersion,
} from '@/api/gen/tessera-backend-api.schemas';
import type { DashboardVignette, TLAIDHProgressOverviewConfig } from '@/components/features/data-harmonization/types';
import type { BaseDiagnostic, DiagnosticExtension } from '@/features/code-editor/types';

// Common types
export type TLAIApiError = {
  error: string;
  type?: string;
  details?: string;
  error_description?: string;
};

export type TLAIFile = {
  program_id?: string;
  filename: string;
  path?: string;
  type?: string;
  download_url?: string;
  created_by?: string;
  created_date?: string;
};

export enum TLAIDomain {
  EDITOR = 'EDITOR',
  WORKFLOWS = 'WORKFLOWS',
  CHAT = 'CHAT',
  MIGRATIONS = 'MIGRATIONS',
  PROJECTS = 'PROJECTS',
  DATA_HARMONIZATION = 'DATA_HARMONIZATION',
  CODE_REMEDIATION = 'CODE_REMEDIATION',
}

export type TLAIDefinedRoles = LiteralUnion<
  | 'editor-access'
  | 'chat-access'
  | 'default-roles-app'
  | 'data-harmonization-view'
  | 'data-harmonization-create'
  | 'data-harmonization-edit'
  | 'data-harmonization-delete'
  | 'data-harmonization-super-admin'
  | 'code-remediation-access'
  | 'cr-lead-access'
  | 'admin'
  | 'offline-access'
  | 'tenant-view'
  | 'tenant-create'
  | 'tenant-manage'
  | 'tenant-admin'
  | 'data-connectors-view'
  | 'data-connectors-create'
  | 'data-connectors-manage'
  | 'data-connectors-delete'
  | 'data-connectors-admin'
  | 'foundry-view'
  | 'foundry-manage'
  | 'foundry-admin'
  | 'workflow-view'
  | 'workflow-create'
  | 'workflow-execute'
  | 'workflow-manage'
  | 'workflow-delete'
  | 'workflow-admin'
  | 'view-remediation-access'
  | 'view-remediation-viewer-access'
  | 'view-remediation-lead-access'
  | 'view-remediation-reviewer'
  | 'view-remediation-admin'
  | 'nexus-view'
  | 'nexus-create'
  | 'nexus-edit'
  | 'nexus-delete'
  | 'nexus-admin'
  | 'nexus-approve'
  | 'llm-usage-dashboard-admin'
  | 'llm-usage-dashboard-viewer'
  | 'kb-editor'
  | 'kb-reviewer'
  | 'kb-admin'
  | 'integration-migration-view'
  | 'integration-migration-create'
  | 'integration-migration-deploy'
  | 'integration-migration-developer'
  | 'dev-tools-view'
  | 'platform-admin',
  string
>;

export type TLAIUserInfo = Omit<KeycloakTokenParsed, 'realm_access'> & {
  email: string;
  family_name: string;
  given_name: string;
  name: string;
  preferred_username: string;
  realm_access?:
    | {
        roles: TLAIDefinedRoles[];
      }
    | TLAIDefinedRoles[];
};

// Login types
export type TLAILoginRequest = {
  username: string;
  password: string;
  redirect_to?: string;
};

export type TLAIRefreshTokenRequest = {
  refresh_token: string;
};

export type TLAILoginResponse = {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  'not-before-policy'?: number;
  session_state?: string;
  scope?: string;
};

export type TLAIRefreshTokenResponse = {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
};

// SSO types
export type TLAISSOProvider = {
  id: string;
  name: string;
  type: string;
};

export type TLAISSOConfig = {
  enabled: boolean;
  providers: TLAISSOProvider[];
  default_provider?: string;
  allow_standard_login: boolean;
};

export type TLAISSOLoginRequest = {
  idp: string;
  redirect_uri: string;
  login_hint?: string;
};

export type TLAISSOLoginResponse = {
  sso_url: string;
  idp: string;
  idp_name: string;
  idp_type: string;
  state: string;
};

export type TLAISSOCallbackRequest = {
  code: string;
  redirect_uri: string;
  state: string;
};

export type TLAISSOCallbackResponse = {
  redirectTo: string;
};

export enum TLAIProgramStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

// ================================
// |       User Preferences       |
// ================================

export type TLAIUserPreferences = {
  id: string;
  tenant_id: string;
  user_email: string;
  preferences: Record<string, unknown>;
  created_date: string;
  updated_date: string;
};

export type TLAIUpdateUserPreferencesRequest = {
  preferences: Record<string, unknown>;
};

export type TLAIUpdateUserPreferencesResponse = TLAIUserPreferences;

// Migration status enum
export enum TLAIMigrationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  DELETED = 'DELETED',
}

export type TLAIProgram = {
  program_id: string;
  filename: string;
  file_type?: TLAIFileType;
  object_type?: string;
  code?: string;
  domain?: TLAIDomain;
  session_id?: string;
  version?: string;
  status?: TLAIProgramStatus;
  state?: TLAIMigrationStatus;
  path?: string;
  created_by?: string;
  updated_by?: string;
  created_date?: string;
  updated_date?: string;
};

export type TLAIMessageCategory =
  | 'QUERY'
  | 'QUESTION'
  | 'MESSAGE'
  | 'OUTPUT'
  | 'PYTHON_CODE'
  | 'PYTHON_OUTPUT'
  | 'ASSISTANT_MESSAGE'
  | 'PREVIEW'
  | 'DOCUMENTATION'
  | 'TRANSLATED_CODE'
  | 'USER_MESSAGE'
  | 'HTML'
  | 'PROCESSING_JOB'
  | 'WORKFLOW'
  | 'ASK_USER_QUESTION'
  | 'VR_WORKFLOW';

export type TLAISuccessResponse = {
  message: string;
  status: 'success';
};

// Feedback endpoint types
export type TLAIFeedbackResponse = {
  success: boolean;
};

// Request types
export type TLAIFeedbackRequest = {
  conversation_id: string;
  feedback: string | null;
};

export type TLAIFileReference = {
  name: string;
  id: string;
};

export type TLAIRemediationChatRequest = {
  message: string;
  active_file: TLAIFileReference;
  conversation_id: string;
  scan_id?: string;
  subobject?: string;
  selected_findings?: TLAIATCFinding[];
  max_tokens?: number;
  stream?: boolean;
  timeout?: number;
};

export type TLAIWorkflowParameterType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'file';

export type TLAIWorkflowParameterDefault = string | number | boolean | Record<string, unknown> | unknown[] | null;

export type TLAIWorkflowInputSchema = {
  type?: 'object';
  properties?: {
    [paramName: string]: {
      type: TLAIWorkflowParameterType;
      default?: TLAIWorkflowParameterDefault;
      description?: string;
      enum?: string[];
      usages?: string[];
    };
  };
  required?: string[];
};

export enum TLAIWorkflowTaskType {
  TOOL = 'TOOL',
  WORKFLOW = 'WORKFLOW',
}

export type TLAIWorkflowDependencyCondition = 'ON_SUCCESS' | 'ON_FAILURE' | 'ALWAYS';
export type TLAIWorkflowStatus = 'ACTIVE' | 'INACTIVE' | 'DRAFT' | 'DELETED';

export enum TLAIWorkflowExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type TLAIWorkflowTaskExecutionStatus = TLAIWorkflowExecutionStatus;

export type TLAIWorkflowExecutionConfig = {
  // For tool tasks:
  tool_name?: string;
  agent_name?: string;
  // For script tasks:
  program_id?: string;
  injection_method?: 'template' | 'header';
  language?: string;
  // For workflow tasks:
  workflow_id?: string;
  // For API tasks:
  endpoint?: string;
  method?: string;
};

export type TLAIWorkflowRetryConfig = {
  max_retries: number;
  retry_delay: number;
};

export type TLAIWorkflowTask = {
  id: string;
  name: string;
  description?: string;
  task_type: TLAIWorkflowTaskType;
  execution_config: TLAIWorkflowExecutionConfig;
  parameter_mappings?: {
    [paramName: string]: string | string[];
  };
  position?: number;
  timeout_seconds?: number;
  retry_config?: TLAIWorkflowRetryConfig;
};

export type TLAIWorkflowDependency = {
  from_task_id: string;
  to_task_id: string;
  condition: TLAIWorkflowDependencyCondition;
};

export type TLAIWorkflowMetadata = {
  source_conversation_id?: string;
  extracted_at?: string;
  [key: string]: string | undefined;
};

export type TLAIWorkflowPreviewResponse = {
  workflow: {
    id?: string;
    name: string;
    description?: string;
    status?: TLAIWorkflowStatus;
    version?: number;
    input_schema: TLAIWorkflowInputSchema;
    workflow_metadata?: TLAIWorkflowMetadata;
    created_by?: string;
    updated_by?: string;
    created_date?: string;
    updated_date?: string;
  };
  tasks: TLAIWorkflowTask[];
  dependencies: TLAIWorkflowDependency[];
  extracted_scripts?: Array<{
    program_id: string;
    filename: string;
    content: string;
    discovered_parameters: string[];
  }>;
  extraction_metadata?: {
    total_messages: number;
    productive_messages: number;
    filtered_tools: string[];
    confidence_score: number;
  };
};

// Create workflow
export type TLAIWorkflowCreateRequest = {
  workflow: {
    name: string;
    description?: string;
    input_schema: TLAIWorkflowInputSchema;
    workflow_metadata?: TLAIWorkflowMetadata;
  };
  tasks: TLAIWorkflowTask[];
  dependencies: TLAIWorkflowDependency[];
  source_conversation_id?: string;
};

// List workflows
export type TLAIWorkflowListRequest = {
  status?: TLAIWorkflowStatus;
  created_by?: string;
  search?: string;
  limit?: number;
  offset?: number;
};

export type TLAIWorkflowListItem = {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_date: string;
  updated_date: string;
  updated_by?: string | null;
  version?: number;
  status: TLAIWorkflowStatus;
  execution_count: number;
  last_executed?: string;
  success_rate?: number;
};

export type TLAIWorkflowGetResponse = {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_date: string;
  updated_date: string;
  updated_by?: string;
  status: TLAIWorkflowStatus;
  version: number;
  input_schema: TLAIWorkflowInputSchema;
  workflow_metadata?: TLAIWorkflowMetadata;
  tasks?: TLAIWorkflowTask[];
  dependencies?: TLAIWorkflowDependency[];
};

// Execute workflow
export type TLAIWorkflowExecuteRequest = {
  parameters: {
    [paramName: string]: any;
  };
  metadata?: {
    [key: string]: any;
  };
};

export type TLAIWorkflowExecuteResponse = {
  id: string;
  workflow_id: string;
  parent_execution_id?: string;
  input_values?: object;
  resolved_values?: object;
  output_values?: object;
  status: TLAIWorkflowExecutionStatus;
  started_at?: string;
  completed_at?: string;
  created_by: string;
  created_date?: string;
  error_details?: object;
};

export type TLAIWorkflowTaskExecution = {
  id: string;
  execution_id: string;
  task_definition_id: string;
  workflow_execution_id: string;
  status: TLAIWorkflowTaskExecutionStatus;
  started_at?: string;
  completed_at?: string;
  task_name: string;
  task_type: string;
  execution_config?: {
    tool_name?: string;
    agent_name?: string;
    [key: string]: any;
  };
  parameter_mappings?: Record<string, any>;
  resolved_parameters?: Record<string, any>;
  input_values?: Record<string, any>;
  output_values?: Record<string, any>;
  output_programs?: string[];
  error_details?: any;
  execution_data?: {
    resolved_parameters?: object;
    output?: any;
    output_programs?: string[];
    error?: string;
    [key: string]: any;
  };
};

export type TLAIWorkflowTaskV2 = {
  id: string;
  workflow_id: string;
  name: string;
  description?: string | null;
  task_type: string;
  execution_config?: {
    tool_name?: string;
    agent_name?: string;
    [key: string]: any;
  };
  parameter_mappings?: Record<string, any>;
  retry_config?: any;
  timeout_seconds?: number | null;
  resource_limits?: any;
  created_by: string;
  updated_by?: string | null;
  created_date: string;
  updated_date?: string | null;
};

export type TLAIWorkflowExecutionGetResponse = {
  execution_id: string;
  workflow_id: string;
  parent_execution_id?: string | null;
  status: TLAIWorkflowExecutionStatus;
  started_at?: string;
  completed_at?: string | null;
  created_by: string;
  created_date: string;
  workflow?: {
    id: string;
    name: string;
    description?: string;
    status: string;
    version?: number;
    input_schema?: Record<string, any>;
    workflow_metadata?: Record<string, any>;
    created_by: string;
    updated_by?: string | null;
    created_date: string;
    updated_date?: string | null;
  };
  input_values?: Record<string, any>;
  resolved_values?: Record<string, any>;
  output_values?: Record<string, any>;
  error_details?: any;
  tasks?: TLAIWorkflowTaskV2[];
  task_executions?: TLAIWorkflowTaskExecution[];
  dependencies?: Array<{
    id: string;
    workflow_id: string;
    from_task_id: string;
    to_task_id: string;
    condition: string;
  }>;
};

export type TLAIWorkflowExecutionsListRequest = {
  workflow_id?: string;
  status?: TLAIWorkflowExecutionStatus;
  executed_by?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
};

export type TLAIAllWorkflowExecutionsInfiniteRequest = {
  status?: string;
  executed_by?: string;
  start_date?: string;
  end_date?: string;
  page_size?: number;
};

export type TLAIWorkflowExecutionListItem = {
  id: string;
  workflow_id: string;
  workflow_name?: string;
  parent_execution_id?: string;
  input_values?: object;
  resolved_values?: object;
  output_values?: object;
  status: TLAIWorkflowExecutionStatus;
  started_at?: string;
  completed_at?: string;
  executed_by?: string;
  created_by: string;
  created_date?: string;
  error_details?: object;
};

export type TLAIAllWorkflowExecutionListItem = {
  execution_id: string;
  workflow_id: string;
  workflow_name: string;
  status: TLAIWorkflowExecutionStatus;
  started_at?: string;
  completed_at?: string;
  executed_by: string;
};

export type TLAIWorkflowExecutionsListResponse = {
  executions: TLAIAllWorkflowExecutionListItem[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TLAIWorkflowSpecificExecutionsResponse = {
  executions: TLAIWorkflowExecutionListItem[];
  total_count: number;
  limit: number;
  offset: number;
};

export type TLAIWorkflowExecutionRetryResponse = {
  success: boolean;
  message: string;
  execution_id: string;
};

export type TLAIWorkflowExecutionCancelResponse = {
  success: boolean;
  message: string;
};

export type TLAIWorkflowExecutionsPaginatedResponse = TLAIWorkflowExecutionsListResponse & {
  pagination: TLAIPagination;
};

export type TLAIWorkflowDeleteResponse = {
  success: boolean;
  message: string;
};

// Execute code types
export type TLAIExecuteCodeRequest = {
  code: string;
  timeout?: number;
  session_id?: string;
};

export type TLAIExecuteCodeResponse = {
  result:
    | {
        [key: string]: string;
      }
    | {
        output: string;
      };
};

// Get programs types
export type TLAIGetProgramsRequest = {
  domain?: TLAIDomain;
  correlation_id?: string;
};

export type TLAIGetProgramsResponse = TLAIProgram[];

// Get program types
export type TLAIGetProgramRequest = {
  program_id: string;
  version?: string;
};

export type TLAIGetProgramResponse = TLAIProgram;

// Save program types
export type TLAISaveProgramRequest = {
  program_id: string;
  domain?: TLAIDomain | null;
  filename?: string | null;
  code?: string | null;
  correlation_id?: string;
  session_id?: string;
};

export type TLAISaveProgramResponse = {
  program_id: string;
  filename: string;
  domain: TLAIDomain;
  created_by: string;
  updated_by: string;
  session_id?: string;
};

// Delete program types
export type TLAIDeleteProgramRequest = {
  program_id: string;
};

export type TLAIDeleteProgramResponse = {
  message: string;
};

// Bulk delete programs types
export type TLAIBulkDeleteProgramsRequest = {
  program_ids: string[];
};

export type TLAIBulkDeleteProgramResult = {
  program_id: string;
  success: boolean;
  error?: string;
};

export type TLAIBulkDeleteProgramsResponse = {
  total: number;
  succeeded: number;
  failed: number;
  results: TLAIBulkDeleteProgramResult[];
};

// Rename program types
export type TLAIRenameProgramRequest = {
  program_id: string;
  new_filename: string;
};

export type TLAIRenameProgramResponse = {
  program_id: string;
  filename: string;
  domain: TLAIDomain;
  created_by: string;
  updated_by: string;
  session_id?: string;
};

// Get programs by migration types
export type TLAIGetProgramsByMigrationRequest = {
  migration_id: string;
};

export type TLAIGetProgramsByMigrationResponse = TLAIProgram[];

// Download program types
export type TLAIDownloadProgramRequest = {
  program_id: string;
  expiration?: number; // in seconds
};

export type TLAIDownloadProgramResponse = {
  program_id: string;
  filename: string;
  presigned_url: string;
  expires_in: number;
  content_type: string;
};

// Migration type
export type TLAIMigration = {
  migration_id: string;
  name: string;
  description: string;
  version_from: TLSAPVersion;
  version_to: TLSAPVersion;
  status: TLAIMigrationStatus;
  created_by: string;
  updated_by: string;
  created_date?: string;
  updated_date?: string;
};

export type TLAIGetMigrationsResponse = TLAIMigration[];

// Create migration types
export type TLAICreateMigrationRequest = {
  name: string;
  description: string;
  version_from: TLSAPVersion;
  version_to: TLSAPVersion;
  files: File[];
};

export type TLAICreateMigrationResponse = {
  success: boolean;
  migration_id: string;
  message?: string;
  error?: string;
};

export type TLAIUpdateMigrationDetailsRequest = {
  migration_id: string;
  name?: string;
  description?: string;
  status?: TLAIMigrationStatus;
};

export type TLAIUpdateMigrationDetailsResponse = {
  success: boolean;
  message?: string;
  error?: string;
};

// Get migration types
export type TLAIGetMigrationRequest = {
  migration_id: string;
};

export type TLAIGetMigrationResponse = TLAIMigration;

// Delete migration types
export type TLAIDeleteMigrationRequest = {
  migration_id: string;
};

export type TLAIDeleteMigrationResponse = {
  success: boolean;
  message?: string;
  error?: string;
};

// Upload programs for migration types
export type TLAIUploadProgramsForMigrationRequest = {
  migration_id: string;
  files: File[];
};

export type TLAIUploadProgramsForMigrationResponse = {
  success: boolean;
  message?: string;
  error?: string;
};

// Upload program types
export type TLAIUploadProgramRequest = {
  files: File[];
  domain: string;
};

export type TLAIUploadProgramResponse = {
  uploaded_files: Array<{
    program_id: string;
    path?: string;
    type?: string;
    filename: string;
  }>;
  failed_files: string[];
};

export type TLAIMessageRole = 'user' | 'assistant';

export type TLAIUserConversation = {
  conversation_id: string;
  title: string;
  created_by: string;
  created_date?: string | null;
  updated_date?: string | null;
};

export enum TLAIHealthStatus {
  OK = 'OK',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export type TLAIHealthState = {
  status: TLAIHealthStatus;
  message?: string;
};

export type TLAIServiceHealthState = Record<string, TLAIHealthState>;

export type TLAIHealthResponse = TLAIServiceHealthState;

export type TLAINextApiLoginResponse = {
  message?: string;
  redirectTo?: string;
};

export type TLAINextApiRefreshResponse = {
  redirectTo?: string;
  message?: string;
  expiresIn?: number;
};

export type TLAINextApiLogoutResponse = {
  success: boolean;
};

export type TLAINextApiHealthResponse = TLAIHealthState;

export type TLAIUserProfile = {
  user_id: string;
  email: string;
  name?: string;
  has_password: boolean;
  is_sso_only: boolean;
  roles: TLAIDefinedRoles[];
  required_actions?: string[];
};

export type TLAIUpdatePasswordRequest = {
  new_password: string;
  current_password?: string;
};

export type TLAICompletePasswordUpdateRequest = {
  change_password_token: string;
  new_password: string;
};

export type TLAIPasswordUpdateRequiredResponse = {
  password_update_required: true;
  change_password_token: string;
  username: string;
};

// ================================
// |           Users              |
// ================================

export type TLAIUserListRequest = {
  first?: number | null;
  max?: number | null;
  search?: string | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  emailVerified?: boolean | null;
  enabled?: boolean | null;
  exact?: boolean | null;
  briefRepresentation?: boolean | null;
  idpAlias?: string | null;
  idpUserId?: string | null;
  q?: string | null;
};

export type TLAIUserRepresentation = {
  id?: string | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  emailVerified?: boolean | null;
  attributes?: Record<string, string[]> | null;
  userProfileMetadata?: unknown | null;
  enabled?: boolean | null;
  self?: string | null;
  origin?: string | null;
  createdTimestamp?: number | null;
  totp?: boolean | null;
  federationLink?: string | null;
  serviceAccountClientId?: string | null;
  credentials?: unknown[] | null;
  disableableCredentialTypes?: string[] | null;
  requiredActions?: string[] | null;
  federatedIdentities?: unknown[] | null;
  realmRoles?: string[] | null;
  clientRoles?: Record<string, string[]> | null;
  clientConsents?: unknown[] | null;
  notBefore?: number | null;
  applicationRoles?: Record<string, string[]> | null;
  socialLinks?: unknown[] | null;
  groups?: string[] | null;
  access?: Record<string, boolean> | null;
};

export type TLAIUserListResponse = {
  users: TLAIUserRepresentation[];
};

// ================================
// |   Code Remediation v3 (FLE)   |
// ================================
export type TLAIRuleResponse = {
  id: string;
  display_name: string;
  description: string;
};

export type TLAIRulesListResponse = {
  rules: TLAIRuleResponse[];
};

export type TLAIChangedLine = {
  line_num: number;
  before: string;
  after: string;
  rule_id?: string | null;
};

export type TLAIFleAnalyzeResponse = {
  changed_lines: TLAIChangedLine[];
  error?: string | null;
};

export type TLAIFleRemediateRequest = {
  remedy_id: string;
  object_id: string; // uuid
  code?: string | null;
  rule_ids?: string[] | null;
};

// New conversation-based types

// Message status enum
export enum TLAIMessageStatus {
  DELIVERED = 'DELIVERED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  TERMINATED = 'TERMINATED',
  STOPPED = 'STOPPED',
  FAILED = 'FAILED',
  DELETED = 'DELETED',
}

export enum TLAIConversationStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

export enum TLAIMessageAgent {
  CHAT = 'chat_agent',
  PLANNER = 'planner_agent',
  SALESFORCE = 'salesforce_agent',
  SALESFORCE_DATA360 = 'salesforce_data360_agent',
  DATA_PROCESSING = 'data_processing_agent',
  DATA_ANALYTICS = 'data_analytics_agent',
  EDITOR = 'code_editor_agent',
  SAP = 'sap_agent',
  D365 = 'd365_agent',
  HANA_BASIS = 'hana_basis_agent',
  SLACK = 'slack_agent',
  GSUITE = 'gsuite_agent',
  JIRA = 'jira_agent',
  MICROSOFT = 'microsoft_agent',
  ORACLE_12C = 'oracle_12c',
  KB = 'kb_agent',
  WORKFLOWS_V2 = 'workflows_v2_agent',
  NEXUS = 'nexus_agent',
  BTP = 'btp_agent',
  VIEW_REMEDIATION = 'view_remediation_agent',
  RELEASE = 'release_agent',
  LANDSCAPE_SYNC_ADVISOR = 'landscape_sync_advisor_agent',
}

export type TLAIConversationFile = {
  program_id: string;
  filename: string;
  download_url: string;
  created_by: string;
  created_date: string;
};

export type TLAIConversationMessageAttachment = {
  filename: string;
  program_id: string;
  created_by?: string;
  created_date?: string;
  file_size?: number | null;
  file_type?: string | null;
};

export type TLAIWorkflowGraph = {
  name?: string | null;
  description?: string | null;
  input_schema?: Record<string, unknown> | null;
  nodes?: TLNodeInput[];
};

/** One selectable option in an ASK_USER_QUESTION clarifying question. */
export type TLAIQuestionOption = {
  label: string;
  description?: string;
};

/** A single clarifying question rendered as a group inside the question card. */
export type TLAIQuestion = {
  question: string;
  header?: string;
  options: TLAIQuestionOption[];
  multiSelect?: boolean;
};

export type TLAIConversationMessage = {
  id: string;
  author: string;
  text: string;
  category: TLAIMessageCategory;
  status: TLAIMessageStatus;
  created_date: string;
  has_attachments: boolean;
  turn_index: number;
  role: TLAIMessageRole;
  parent_message_id?: string | null;
  agent?: TLAIMessageAgent | null;
  tool?: string | null;
  attachments?: string[];
  processing_time_ms?: number | null;
  thoughts?: string | null;
  /** Structured workflow graph for WORKFLOW messages — loaded onto the Loom canvas. */
  workflow_graph?: TLAIWorkflowGraph | null;
  /** When set on a WORKFLOW message, open its graph as a NEW workflow (a copy) rather than the current canvas. */
  open_as_new_workflow?: boolean | null;
  /** Clarifying multiple-choice questions for ASK_USER_QUESTION messages — rendered as a card. */
  questions?: TLAIQuestion[] | null;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
};

export type TLAIConversationMetadata = {
  /** Loom workflow the chat was opened against. */
  workflow_id?: string | null;
  /** Loom workflow template the chat was opened against. */
  template_id?: string | null;
  /** Nexus blueprint the chat was opened against. */
  project_id?: string | null;
};

export type TLAIConversationSession = {
  conversation_id: string;
  title: string;
  status: TLAIConversationStatus;
  created_by: string;
  created_date?: string | null;
  updated_date?: string | null;
  metadata?: TLAIConversationMetadata | null;
};

export enum TLAIConversationType {
  CHAT = 'CHAT',
  EDITOR_CHAT = 'EDITOR_CHAT',
  REMEDIATION_CHAT = 'REMEDIATION_CHAT',
  DATA_HARMONIZATION_CHAT = 'DATA_HARMONIZATION_CHAT',
}

export type TLAIConversationListRequest = {
  conversation_type?: TLConversationType;
};

export type TLAIConversationListResponse = {
  sessions: TLAIConversationSession[];
};

export type TLAIConversationDetailsRequest = {
  conversation_id: string;
  last_updated?: string;
};

export type TLAIConversationDetailsResponse = {
  conversation_name?: string;
  history: TLAIConversationMessage[];
  files: TLAIConversationMessageAttachment[];
  serverTime?: string;
  conversation_type?: TLConversationType;
  metadata?: TLAIConversationMetadata | null;
  /** LLM profile this conversation is bound to (null = default). Locked after the first message. */
  llm_model_cost_id?: string | null;
  /** Stable llm_models model_id resolved from the bound cost id (null = unbound or catalog gone). */
  llm_model_id?: string | null;
  /** Underlying model slug from llm_models; resolves even for retired or repriced models. */
  llm_model?: string | null;
  /** Operator-set friendly name from llm_models for the bound model. */
  llm_model_display_name?: string | null;
  /** Catalog lifecycle status of the bound model (ACTIVE | DEPRECATED | RETIRED). DEPRECATED keeps
   * running; RETIRED routes new messages to the default. Absent on older backends. */
  llm_model_status?: string | null;
  /** True once the conversation has been summarized enough times that further sends are rejected until a
   * new chat is started. Optional — absent on older backends, treated as not-at-limit. */
  compaction_limit_reached?: boolean;
};

export type TLAIConversationChatRequest = {
  conversation_id?: string;
  message: string;
  turn_index?: number;
  attachments?: string[];
};

export type { TLSendMessageRequest, TLSendMessageRequestContext };

export type TLAIConversationChatResponse = {
  conversation_id: string;
  message_id: string;
  message_status: TLAIMessageStatus;
  session_name?: string | null;
  status?: TLAIConversationStatus;
  turn_index?: number;
  server_time?: string | null;
};

export type TLAIRenameConversationRequest = {
  conversation_id: string;
  new_conversation_name: string;
};

export type TLAIRenameConversationResponse = {
  success: boolean;
  message: string;
  new_title: string;
  old_title: string;
};

export type TLAILinkConversationWorkflowRequest = {
  conversation_id: string;
  workflow_id: string;
  /** The linked conversation's type, so the optimistic cache write targets that type's list. */
  conversation_type: TLConversationType;
};

export type TLAILinkConversationTemplateRequest = {
  conversation_id: string;
  template_id: string;
  /** The linked conversation's type, so the optimistic cache write targets that type's list. */
  conversation_type: TLConversationType;
};

export type TLAIDeleteConversationRequest = {
  conversation_id: string;
};

export type TLAIDeleteConversationResponse = {
  success: boolean;
  message: string;
};

export type TLAIForkConversationRequest = {
  conversation_id: string;
  title?: string | null;
  up_to_message_id?: string | null;
  inclusive?: boolean;
};

export type TLAIForkConversationResponse = {
  conversation_id: string;
  title: string;
  message_count: number;
  copied_attachments: number;
  missing_attachments: string[];
};

export type TLAIDeleteAllConversationsRequest = Record<string, never>;

export type TLAIDeleteAllConversationsResponse = {
  success: boolean;
  message: string;
  deleted_count?: number;
};

export type TLAIConversationFilePreviewRequest = {
  conversation_id: string;
  program_id: string;
  // max (10000) -- min (1) -- default (100)
  limit?: number;
  offset?: number;
  search?: string;
};

export type TLAIConversationFilePreviewResponse = {
  program_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  is_truncated: boolean;
  preview: TLAIFilePreview;
  capabilities?: TLPreviewCapabilities;
  created_date?: string | null;
  updated_date?: string | null;
};

export type TLAIConversationFilesRequest = {
  conversation_id: string;
};

export type TLAIConversationFilesResponse = TLAIConversationFile[];

export type TLAICancelConversationRequest = {
  conversation_id: string;
  message_id: string;
};

export type TLAICancelConversationResponse = {
  success: boolean;
  message: string;
};

export type TLAIDeleteConversationMessageRequest = {
  conversation_id: string;
  message_id: string;
};

export type TLAIDeleteConversationMessageResponse = {
  success: boolean;
  message: string;
};

export type TLAIMessageFeedbackRequest = {
  conversation_id: string;
  message_id: string;
  is_positive: boolean;
};

export type TLAIMessageFeedbackResponse = {
  message: string;
  status: string;
};

// Preview Types

// Base interface for all file type previews
export type TLAIBaseFileTypePreview = {
  type: string;
  message: string;
};

// Text file preview
export type TLAITextPreview = TLAIBaseFileTypePreview & {
  type: 'text';
  lines: string[];
  total_lines: number;
  is_truncated: boolean;
};

// Markdown file preview (rendered/raw toggle)
export type TLAIMarkdownPreview = TLAIBaseFileTypePreview & {
  type: 'markdown';
  lines: string[];
  total_lines: number;
  is_truncated: boolean;
};

// HTML file preview (rendered/raw toggle)
export type TLAIHtmlPreview = TLAIBaseFileTypePreview & {
  type: 'html';
  lines: string[];
  total_lines: number;
  is_truncated: boolean;
};

// JSON file preview
export type TLAIJSONPreview = TLAIBaseFileTypePreview & {
  type: 'json';
  content: any; // Parsed JSON content
  formatted: string; // Formatted JSON string for display
  is_truncated: boolean;
};

// Code file preview
export type TLAICodePreview = TLAIBaseFileTypePreview & {
  type: 'code';
  language: string; // Programming language of the code file
  lines: string[];
  total_lines: number;
  is_truncated: boolean;
};

// Binary file preview
export type TLAIBinaryPreview = TLAIBaseFileTypePreview & {
  type: 'binary';
  message: string; // Message indicating this is a binary file
  content_preview?: string; // Optional content preview for certain binary formats
};

// Empty file preview
export type TLAIEmptyPreview = TLAIBaseFileTypePreview & {
  type: 'empty';
  message: string; // Message indicating the file is empty
};

// Word-processor document preview — extracted Markdown, rendered or raw
export type TLAIDocumentPreview = TLAIBaseFileTypePreview & {
  type: 'document';
  lines: string[]; // Markdown lines extracted from the document
  total_lines: number; // Total number of extracted lines
  is_truncated: boolean; // Whether the extracted text was truncated
};

// Image file preview
export type TLAIImagePreview = TLAIBaseFileTypePreview & {
  type: 'image';
  url: string; // URL of the image
};

// PDF file preview
export type TLAIPDFPreview = TLAIBaseFileTypePreview & {
  type: 'pdf';
  url: string; // URL of the PDF file
};

// Preview error
export type TLAIPreviewError = TLAIBaseFileTypePreview & {
  type: 'error';
  message: string; // Error message
};

// Parquet column information
export type TLAIParquetColumnInfo = {
  name: string; // Name of the column
  type: string; // Type of the column
  non_null: number; // Number of non-null values in the column
  null: number; // Number of null values in the column
};

// Parquet file preview
export type TLAIParquetPreview = TLAIBaseFileTypePreview & {
  type: 'parquet';
  total_rows: number; // Total rows of the file
  total_columns: number; // Total columns of the file
  columns: TLAIParquetColumnInfo[]; // Columns of the file
  sample_data: Record<string, any>[]; // Sample data of the file
  sample_rows_shown: number; // Sample rows shown of the file
};

// Xlsx/xls sheet data
export type TLAIXlsxSheetPreview = {
  name: string; // Sheet name
  headers: string[]; // Column headers
  rows: string[][]; // Row data, stringified
  total_rows: number; // Rows in the sheet before truncation
  is_truncated: boolean; // This sheet's rows exceeded the per-sheet cap
};

// Xlsx/xls file preview
export type TLAIXlsxPreview = TLAIBaseFileTypePreview & {
  type: 'xlsx';
  total_sheets: number; // Sheet count in the workbook before truncation
  sheet_names: string[]; // Names of sheets actually returned
  sheets: TLAIXlsxSheetPreview[];
  is_truncated: boolean; // Sheets truncated OR any sheet.is_truncated
};

// Union type for all preview types
export type TLAIFilePreview =
  | TLAITextPreview
  | TLAIMarkdownPreview
  | TLAIDocumentPreview
  | TLAIHtmlPreview
  | TLAIJSONPreview
  | TLAICodePreview
  | TLAIBinaryPreview
  | TLAIEmptyPreview
  | TLAIImagePreview
  | TLAIPDFPreview
  | TLAIPreviewError
  | TLAIParquetPreview
  | TLAIXlsxPreview;
export type TLAIPagination = {
  page_size: number;
  offset: number;
  next?: number | null;
  previous?: number | null;
  total?: number | null;
};

export type TLAIPaginationRequest<T extends object = object> = T & {
  page_size?: number;
  offset?: number;
};

export type TLAIPaginatedResponse<T extends object = object> = T & {
  pagination: TLAIPagination;
};

export type TLAIPaginatedData<T> = TLAIPaginatedResponse<{ data: T[] }>;

export enum TLAIERPDomain {
  SAP = 'SAP',
  SALESFORCE = 'SALESFORCE',
  WORKDAY = 'WORKDAY',
}

export enum TLAIFileType {
  ABAP = 'abap',
  PYTHON = 'py',
  MARKDOWN = 'md',
  CSV = 'csv',
  PARQUET = 'parquet',
  DB = 'db',
  DUCKDB = 'duckdb',
  TXT = 'txt',
  XLS = 'xls',
  XLSX = 'xlsx',
  PDF = 'pdf',
  RTF = 'rtf',
  DOC = 'doc',
  DOCX = 'docx',
  JSON = 'json',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  BMP = 'bmp',
  TIF = 'tif',
  TIFF = 'tiff',
  SVG = 'svg',
  ICO = 'ico',
  WEBP = 'webp',
  HEIC = 'heic',
  AVIF = 'avif',
  TSV = 'tsv',
}

type TLAIBaseCredentialsResponse = {
  id: string;
  user_id: string;
  user_email: string;
  username: string;
  created_date: string;
  updated_date: string;
  domain: TLAIERPDomain;
};

export type TLAISAPCredentialsResponse = TLAIBaseCredentialsResponse & {
  domain: TLAIERPDomain.SAP;
  system_version: TLSAPVersion;
};

export type TLAISalesforceCredentialsResponse = TLAIBaseCredentialsResponse & {
  domain: TLAIERPDomain.SALESFORCE;
};

export type TLAIUserCredential = TLAISAPCredentialsResponse | TLAISalesforceCredentialsResponse;

export type TLAIUserCredentialsResponse = {
  credentials: TLAIUserCredential[];
  total_count: number;
};

export type TLAICreateSAPCredentialsRequest = {
  username: string;
  password: string;
  system_version: TLSAPVersion;
};

export type TLAICreateSAPCredentialsResponse = TLAISAPCredentialsResponse;

export type TLAICreateSalesforceCredentialsRequest = {
  username: string;
  password: string;
  security_token: string;
};

export type TLAICreateSalesforceCredentialsResponse = TLAISalesforceCredentialsResponse;

export type TLAIDeleteCredentialsRequest = {
  credentials_id: string;
};

export enum TLAICodeScanItemPriority {
  HIGH = '1',
  MEDIUM = '2',
  LOW = '3',
}

export enum TLAICodeScanItemQuickFixAvailability {
  AUTOMATIC = 'Automatic Quickfixes Available',
  MANUAL = 'Manual Quickfixes Available',
  NO = 'No Quickfixes Available',
}

export enum TLAICodeScanItemChangeCategory {
  TECHNICAL = 'Technical',
  FUNCTIONAL = 'Functional',
  OTHER = 'Other',
}

export type TLAICodeSyntaxCheckRequest = {
  program_id: string;
  sap_system_version?: TLSAPVersion;
};

export type TLAICodeSyntaxCheckResponse = {
  success: boolean;
  program_name: string;
  sap_system_version: TLSAPVersion;
  has_syntax_errors: boolean;
  error_line?: string | null;
  error_message?: string | null;
  error_details?: string | null;
};

export type TLAICodeManageProgramRequest = {
  program_id: string;
  sap_system_version?: TLSAPVersion;
};

export enum TLAISAPParameterSign {
  INCLUDE = 'I',
  EXCLUDE = 'E',
}

export enum TLAISAPParameterOption {
  EQUAL = 'EQ',
  NOT_EQUAL = 'NE',
  GREATER_EQUAL = 'GE',
  GREATER_THAN = 'GT',
  LESS_EQUAL = 'LE',
  CONTAINS_PATTERN = 'CP',
  NOT_CONTAINS_PATTERN = 'NP',
  BETWEEN = 'BT',
  NOT_BETWEEN = 'NB',
}

export type TLAISAPParameter = {
  SELNAME: string;
  KIND: string;
  SIGN?: `${TLAISAPParameterSign}` | null;
  OPTION?: `${TLAISAPParameterOption}` | null;
  LOW?: string | null;
  HIGH?: string | null;
};

export type TLAICodeProgramParameters = {
  parameters: TLAISAPParameter[];
};

export type TLAICodeManageProgramResponse = {
  success: boolean;
  program_name: string;
  sap_system_version: TLSAPVersion;
  syntax_check_status: boolean;
  syntax_error_line?: string | null;
  syntax_error_message?: string | null;
  action?: string | null;
  params_result?: TLAICodeProgramParameters | null;
  error_details?: string | null;
};

export type TLAICodeExecuteProgramRequest = {
  program_id: string;
  input_params: TLAICodeProgramParameters;
  sap_system_version?: TLSAPVersion;
};

export type TLAICodeExecuteProgramResponse = {
  success: boolean;
  program_id?: string | null;
  message?: string | null;
};

export type TLAIUploadSAPNotesRequest = {
  files: File[];
  note_numbers: string[];
};

export type TLAIUploadSAPNotesItem = {
  note_name: string;
  success: boolean;
};

export type TLAIUploadSAPNotesResponse = {
  uploaded_notes: TLAIUploadSAPNotesItem[];
  total_count: number;
  successful_count: number;
  failed_count: number;
  status: string;
  message: string;
};

export type TLAISAPNote = {
  name: string;
  url?: string | null;
};

export type TLAIListSAPNotesResponse = {
  notes: TLAISAPNote[];
  total_count: number;
};

export type TLAIDeleteSapNoteResponse = {
  success: boolean;
  message: string;
  note_name: string;
};

export type TLAIMissingSAPNotesRequest = {
  scan_id: string;
};

export type TLAIMissingSAPNotesResponse = {
  scan_id: string;
  required_notes: string[];
  available_notes: string[];
  missing_notes: string[];
  total_required: number;
  total_available: number;
  total_missing: number;
};

export type TLAISubmitATCScanRequest = {
  check_variant?: string;
  package_name?: string;
  obj_name?: string;
  component?: string;
  sap_system_version: TLSAPVersion;
};

export type TLAISubmitGlobalATCScanRequest = {
  sap_system_version: TLSAPVersion;
};

export type TLAISubmitATCScanResponse = {
  scan_id: string;
};

export type TLAISubmitGlobalATCScanResponse = {
  scan_id: string;
};

export type TLAIDuplicateScanRequest = {
  scan_id: string;
  duplicate_count: number;
};

export type TLAIDuplicateScanResponse = {
  success: boolean;
  message: string;
  duplicated_scan_ids: string[];
  total_duplicated: number;
};

export type TLAIGetATCScanRequest = {
  scan_id: string;
};

export type TLAIATCScanStatus = 'SUBMITTED' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type TLAIATCScanTransportStatus = 'PENDING' | 'CREATED' | 'APPLIED';

export type TLAIATCScanDetails = {
  check_variant: string;
  package_name?: string | null;
  obj_name?: string | null;
  component?: string | null;
  sap_system_version: TLSAPVersion;
};

export type TLAIATCFinding = {
  finding_id: string;
  OBJECT_NAME: string;
  OBJTYPE: string;
  PACKAGE: string;
  CHECK_TITLE: string;
  CHECK_MESSAGE: string;
  SUBOBJECT: string;
  LINE: string;
  SAP_NOTE_NUMBER?: string | null;
  PRIORITY?: string | null;
  SUBOBJECTIVE?: string | null;
};

export type TLAIPerformATCCheckForManualFixRequest = {
  code: string;
  object_name: string;
  subobject: string;
  package: string;
  objtype: string;
  scan_id: string;
};

export type TLAIPerformATCCheckForManualFixResponse = {
  findings: TLAIATCFinding[];
};

export type TLAIGetProgramCodeContextRequest = {
  finding_id: string;
  object_name: string;
  objtype: string;
  package: string;
  check_title: string;
  check_message: string;
  subobject: string;
  line: string;
  sap_note_number?: string | null;
  priority?: string | null;
  subobjective?: string | null;
};

export type TLAIGetProgramCodeContextResponse = {
  code_context: string;
};

export type TLAIGetFullProgramContentRequest = {
  scan_id: string;
  subobject: string;
  package?: string;
  objtype?: string;
  findings: {
    finding_id: string;
    object_name: string;
    objtype: string;
    package: string;
    check_title: string;
    check_message: string;
    subobject: string;
    line: string;
    sap_note_number?: string | null;
    priority?: string | null;
  }[];
};

export type TLAIGetFullProgramContentResponse = {
  program_content: string;
  error_lines: number[];
  program_name: string;
};

export type TLAIGetATCScanResponse = {
  scan_id: string;
  status: TLAIATCScanStatus;
  findings?: TLAIATCFinding[] | null;
  total_findings: number;
  error_details?: string | null;
  transport_status?: TLAIATCScanTransportStatus | null;
  scan_details?: TLAIATCScanDetails | null;
  last_remediation_id?: string | null;
  last_apply_changes_job_id?: string | null;
};

export type TLAIRemediationStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type TLAIRemediationMinimal = {
  remediation_id: string;
  status: TLAIRemediationStatus;
};

export type TLAIATCScan = {
  scan_id: string;
  status: TLAIATCScanStatus;
  package_name: string;
  check_variant: string;
  obj_name: string;
  component: string;
  sap_system_version: TLSAPVersion;
  scan_started_at: string;
  scan_completed_at?: string | null;
  error_message?: string | null;
  remediations?: TLAIRemediationMinimal[] | null;
};

export type TLAIApplyRemediationRequest = {
  scan_id: string;
  remediation_id: string;
};

export type TLAIApplyRemediationResponse = {
  success: boolean;
  job_id?: string | null;
  scan_id: string;
  transport_number?: string | null;
  message?: string | null;
  error_message?: string | null;
};

export type TLAIApplyRemediationStatusRequest = {
  job_id: string;
};

export type TLAIApplyRemediationStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'PARTIAL_SUCCESS'
  | 'SUCCESS';

export type TLAIApplyRemediationStatusResponse = {
  job_id: string;
  scan_id: string;
  status: TLAIApplyRemediationStatus;
  transport_number?: string | null;
  total_programs: number;
  successful_updates: number;
  failed_updates: number;
  programs_updated: string[];
  programs_failed: string[];
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
};

export type TLAIListUserScansResponse = {
  scans: TLAIATCScan[];
  total: number;
};

export type TLAILatestATCScanResponse =
  | {
      scan_id: null;
    }
  | TLAIGetATCScanResponse;

export type TLAIExportScanCsvRequest = {
  scan_id: string;
};

export type TLAIExportScanCsvResponse = {
  success: boolean;
  program_id: string;
  scan_id: string;
  total_findings: number;
  filename: string;
  message: string;
};

export type TLAISubmitRemediationRequest = {
  scan_id: string;
  findings: TLAIATCFinding['finding_id'][];
  instructions?: string;
};

export type TLAISubmitRemediationResponse = {
  remediation_id: string;
};

export type TLAIGetRemediationRequest = {
  remediation_id: string;
};

export type TLAIRemediationStatusRequest = {
  remediation_id: string;
};

export type TLAIRemediationSyntaxCheck = 'PASS' | 'FAIL' | 'SKIPPED';

export type TLAIRemediationResult = {
  original_program_name: string;
  modified_program_id: string;
  syntax_check: TLAIRemediationSyntaxCheck;
  syntax_check_message: string;
};

export type TLAIRemediationStatusResponse = {
  remediation_id: string;
  finding_ids: string[];
  status: TLAIRemediationStatus;
  remediation_results: TLAIRemediationResult[];
  total_programs_found?: number | null;
  programs_completed?: number | null;
  progress_percentage?: number | null;
};

export type TLAIRemediationsForScanRequest = {
  scan_id: string;
};

export type TLAIRemediationSimple = {
  id: string;
  findings: string[];
};

export type TLAIRemediationsForScanResponse = {
  remediations: TLAIRemediationSimple[];
};

export type TLAICodeRemediationPreviewRequest = {
  remediation_id: string;
  object_name: string;
};

export type TLAICodeRemediationPreviewResponse = {
  remediation_id: string;
  original_program_name: string;
  original_code: string;
  modified_code: string;
  code_diff: string;
  remediation_success: boolean;
  syntax_check_message: string;
};

export type TLAICancelScanRequest = {
  scan_id: string;
  message?: string | null;
};

export type TLAICancelScanResponse = {
  scan_id: string;
  success: boolean;
  message: string;
};

export type TLAICancelRemediationRequest = {
  remediation_id: string;
  message?: string | null;
};

export type TLAICancelRemediationResponse = {
  remediation_id: string;
  success: boolean;
  message: string;
};

export enum TLAIPropertyType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  LIST = 'list',
  SECRET = 'secret',
}

export enum TLAITenantStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

export type TLAITenantProperty = {
  key: string;
} & (
  | {
      type: TLAIPropertyType.STRING;
      value: string;
    }
  | {
      type: TLAIPropertyType.NUMBER;
      value: number;
    }
  | {
      type: TLAIPropertyType.BOOLEAN;
      value: boolean;
    }
  | {
      type: TLAIPropertyType.LIST;
      value: string;
    }
  | {
      type: TLAIPropertyType.SECRET;
      value: string;
    }
);

export type TLAITenantConfiguration = TLAITenantProperty[];

export type TLAITenant = {
  tenant_id: string;
  status: TLAITenantStatus;
  configuration: TLAITenantConfiguration;
};

export type TLAITenantListResponse = TLAITenant[];

export type TLAICreateTenantRequest = {
  tenant_id: string;
  configuration: TLAITenantConfiguration;
};

export type TLAICreateTenantResponse = TLAITenant;

export type TLAIUpdateTenantRequest = {
  tenant_id: string;
  configuration?: TLAITenantConfiguration;
  status?: TLAITenantStatus;
};

export type TLAIUpdateTenantResponse = TLAITenant;

export type TLAIDeleteTenantRequest = {
  tenant_id: string;
};

export type TLAIDeleteTenantResponse = TLAISuccessResponse;

export enum TLAIPromptTemplateCellType {
  TEXT = 'TEXT',
  INPUT = 'INPUT',
}

export type TLAIPromptTemplateCell = {
  type: TLAIPromptTemplateCellType;
  label: string;
  description: string;
  value: string;
};

export type TLAIPromptTemplate = {
  id: string;
  name: string;
  description: string;
  configuration: TLAIPromptTemplateCell[];
  is_public: boolean;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export type TLAIPromptTemplateListResponse = TLAIPromptTemplate[];

export type TLAIPromptTemplateCreateRequest = {
  name: string;
  description: string;
  configuration: TLAIPromptTemplateCell[];
  is_public: boolean;
};

export type TLAIPromptTemplateCreateResponse = TLAIPromptTemplate;

export type TLAIPromptTemplateUpdateRequest = {
  id: string;
  name: string;
  description: string;
  configuration: TLAIPromptTemplateCell[];
  is_public: boolean;
};

export type TLAIPromptTemplateUpdateResponse = TLAIPromptTemplate;

export type TLAIPromptTemplateDeleteRequest = {
  id: string;
};

export type TLAIPromptTemplateDeleteResponse = TLAISuccessResponse;

export type TLAIAddToTransportRequestRequest = {
  program_id: string;
  tr_description: string;
  object_name: string;
  object_type: string;
  sap_system_version: TLSAPVersion;
};

export type TLAIAddToTransportRequestResponse = TLAISuccessResponse;

export enum TLAIObjectCategory {
  R3TR = 'R3TR', // Full Object
  LIMU = 'LIMU', // Subobject of the development environment
  LANG = 'LANG', // Language-specific component
  CORR = 'CORR', // Comment (Automatic)
}

export type TLAIFixTargetFilter = {
  field: string;
  value: string;
  custom_field?: string | null;
};

export type TLAIFixExample = {
  code_with_issue: string;
  fixed_code: string;
};

export type TLAIFixTemplate = {
  fix_template_id: string;
  title: string;
  description?: string | null;
  target_filters: TLAIFixTargetFilter[];
  prompt: string;
  example?: TLAIFixExample[] | null;
};

export type TLAIFixTemplateListResponse = {
  fix_examples: TLAIFixTemplate[];
};

export type TLAIFixTemplateCreateRequest = {
  title: string;
  description?: string | null;
  prompt: string;
  target_filters: TLAIFixTargetFilter[];
  examples?: TLAIFixExample[] | null;
};

export type TLAIFixTemplateCreateResponse = TLAISuccessResponse;

export type TLAIFixTemplateUpdateRequest = {
  fix_template_id: string;
  title: string;
  description?: string | null;
  prompt: string;
  target_filters: TLAIFixTargetFilter[];
  examples?: TLAIFixExample[] | null;
};

export type TLAIFixTemplateUpdateResponse = TLAIFixTemplate;

export type TLAIFixTemplateDeleteRequest = {
  fix_template_id: string;
};

export type TLAIFixTemplateDeleteResponse = TLAISuccessResponse;

/** Wire format for a fix-library export. Only JSONL can be imported back. */
export enum TLAIFixLibraryExportFormat {
  JSONL = 'JSONL',
  CSV = 'CSV',
}

/** What an import does with a row whose title the library already holds. */
export enum TLAIFixLibraryImportConflict {
  IGNORE = 'IGNORE',
  OVERWRITE = 'OVERWRITE',
  RENAME = 'RENAME',
}

/** What an import actually did with one row. Absent when the row failed. */
export enum TLAIFixLibraryImportAction {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  SKIPPED = 'SKIPPED',
}

export type TLAIFixLibraryImportRowResult = {
  success: boolean;
  error?: string | null;
  line: number;
  title?: string | null;
  /** The title the rule was stored under; differs from `title` only under RENAME. */
  stored_title?: string | null;
  action?: TLAIFixLibraryImportAction | null;
};

/**
 * The house bulk envelope. `succeeded` counts every row that did not fail, so it includes
 * skips — the created/updated/skipped breakdown comes from `results[].action`.
 */
export type TLAIFixLibraryImportResponse = {
  total: number;
  succeeded: number;
  failed: number;
  results: TLAIFixLibraryImportRowResult[];
};

export type TLAIFixLibraryImportRequest = {
  file: File;
  on_conflict: TLAIFixLibraryImportConflict;
};

export type TLAIFixLibraryExportSelectionRequest = {
  fix_template_ids: string[];
};

// ================================
// |     Data Harmonization       |
// ================================
export enum TLAIDataHarmonizationDataCheckPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}
export enum TLAIDataHarmonizationDataCheckStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

export enum TLAIDataHarmonizationDataCheckExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum TLAIDataHarmonizationLibraryStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

export enum TLAIDataHarmonizationLibraryType {
  CUSTOM = 'CUSTOM',
  DEFAULT = 'DEFAULT',
}

export type TLAIDataHarmonizationDashboardUsage = {
  dashboard_id: string;
  dashboard_name: string;
};

export enum TLAIDataHarmonizationDataCheckType {
  RFC = 'RFC',
  CUSTOM_RFC = 'CUSTOM_RFC',
  FILE_UPLOAD = 'FILE_UPLOAD',
  ODATA = 'ODATA',
  FOUNDRY = 'FOUNDRY',
}

export enum TLAICountSourceType {
  LITERAL = 'LITERAL',
  QUERY = 'QUERY',
}

/**
 * A configurable source for a data check count (total scan or issue count):
 * either a hardcoded number (LITERAL) or a Foundry SQL query returning a single
 * number (QUERY). Applies to FOUNDRY and FILE_UPLOAD checks; when unset the
 * execution falls back to the result-dataset row count.
 */
export type TLAICountSource = {
  type: TLAICountSourceType;
  literal_value?: number | null;
  query?: string | null;
};

export type TLAIDHCheckMetadata = {
  recommendation?: string;
  business_impact?: string;
  issue_description?: string;
  data_check_type?: TLAIDataHarmonizationDataCheckType;
  sap_program_name?: string;
  rfc_name?: string;
  raw_uploaded_program_id?: string;
  select_relevant?: string;
  select_result?: string;
  table_alias?: string;
  processing_logic?: string;
  total_scan_source?: TLAICountSource | null;
  issue_count_source?: TLAICountSource | null;
  [key: string]: any;
};

export type TLAIValidateCountQueryRequest = {
  query: string;
};

export type TLAIValidateCountQueryResponse = {
  valid: boolean;
  value?: number | null;
  error?: string | null;
};

export type TLAIDataHarmonizationDataCheck = {
  data_check_id: string;
  name: string;
  status: TLAIDataHarmonizationDataCheckStatus;
  library_id: string;
  category: TLAIDataHarmonizationDataCheckCategory;
  dimension: TLAIDataHarmonizationDimension;
  program_id: string;
  check_metadata: TLAIDHCheckMetadata;
  is_global: boolean;
  priority?: TLAIDataHarmonizationDataCheckPriority;
  rule_id?: string;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
  usages?: TLAIDataHarmonizationDashboardUsage[];
};

export type TLAIDataHarmonizationDataCheckCategory = {
  category_id: string;
  name: string;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export enum TLAIDataHarmonizationDashboardStatus {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
}

export type TLAIDataHarmonizationDashboard = {
  dashboard_id: string;
  name: string;
  description?: string;
  workstream?: string;
  dashboard_type?: TLDashboardType;
  status: TLAIDataHarmonizationDashboardStatus;
  baseline_score?: number;
  score?: number;
  target_score?: number;
  config?: {
    config_id: string;
    dashboard_id: string;
    graph_config: TLAIDHProgressOverviewConfig;
    settings: any;
  };
  vignette?: DashboardVignette;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export type TLAIDataHarmonizationDataCheckExecution = {
  execution_id: string;
  data_check_id: string;
  dashboard_id?: string;
  mapping_id?: string;
  status: TLAIDataHarmonizationDataCheckExecutionStatus;
  result?: Record<string, any>;
  result_program_id?: string;
  parameters?: Record<string, any>;
  started_date?: string;
  completed_date?: string;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export type TLAIDataHarmonizationLibrary = {
  library_id: string;
  name: string;
  description?: string;
  status: TLAIDataHarmonizationLibraryStatus;
  type: TLAIDataHarmonizationLibraryType;
  data_check_count: number;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export type TLAIDataHarmonizationDimension = {
  dimension_id: string;
  name: string;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

// ============ Categories ============
export type TLAIGetAllDHCategoriesResponse = TLAIDataHarmonizationDataCheckCategory[];

export type TLAICreateDHCategoryRequest = {
  name: string;
};

export type TLAICreateDHCategoryResponse = TLAIDataHarmonizationDataCheckCategory;

export type TLAIUpdateDHCategoryRequest = {
  category_id: string;
  name?: string;
};

export type TLAIUpdateDHCategoryResponse = TLAIDataHarmonizationDataCheckCategory;

export type TLAIDeleteDHCategoryRequest = {
  category_id: string;
};

export type TLAIDeleteDHCategoryResponse = TLAISuccessResponse;

// ============ Dimensions ============
export type TLAIGetAllDHDimensionsResponse = TLAIDataHarmonizationDimension[];

export type TLAICreateDHDimensionRequest = {
  name: string;
};

export type TLAICreateDHDimensionResponse = TLAIDataHarmonizationDimension;

export type TLAIUpdateDHDimensionRequest = {
  dimension_id: string;
  name: string;
};

export type TLAIUpdateDHDimensionResponse = TLAIDataHarmonizationDimension;

export type TLAIDeleteDHDimensionRequest = {
  dimension_id: string;
};

export type TLAIDeleteDHDimensionResponse = TLAISuccessResponse;

// ============ Libraries ============
export type TLAIGetAllDHLibrariesResponse = {
  libraries: TLAIDataHarmonizationLibrary[];
};

export type TLAICreateDHLibraryRequest = {
  name: string;
  description?: string;
  type: TLAIDataHarmonizationLibraryType;
  program_ids?: string[];
};

export type TLAICreateDHLibraryResponse = TLAIDataHarmonizationLibrary;

export type TLAIGetDHLibraryRequest = {
  library_id: string;
};

export type TLAIGetDHLibraryResponse = TLAIDataHarmonizationLibrary;

export type TLAIUpdateDHLibraryRequest = {
  library_id: string;
  name?: string;
  description?: string;
  status?: TLAIDataHarmonizationLibraryStatus;
};

export type TLAIUpdateDHLibraryResponse = TLAIDataHarmonizationLibrary;

export type TLAIDeleteDHLibraryRequest = {
  library_id: string;
};

export type TLAIDeleteDHLibraryResponse = TLAISuccessResponse;

export type TLAICreateDataCheckType = {
  name: string;
  program_id?: string;
  priority?: string;
  category_id?: string;
  dimension_id?: string;
  metadata?: TLAIDHCheckMetadata;
  rule_id?: string;
};

export type TLAIAddDataCheckToLibraryRequest = {
  library_id: string;
  data_checks: TLAICreateDataCheckType[];
};

export type TLAIAddDataCheckToLibraryResponse = TLAISuccessResponse;

export type TLAIGetLibraryDataChecksRequest = {
  library_id: string;
};

export type TLAIGetLibraryDataChecksResponse = {
  data_checks: TLAIDataHarmonizationDataCheck[];
};

// ============== Data Checks ==============
export type TLAIGetAllDataChecksRequest = {
  library_ids?: string[];
};

export type TLAIGetAllDataChecksResponse = {
  data_checks: TLAIDataHarmonizationDataCheck[];
};

export type TLAIUpdateDataCheckRequest = {
  name?: string;
  priority?: string;
  category_id?: string;
  dimension_id?: string;
  rule_id?: string;
  metadata?: TLAIDHCheckMetadata;
  is_global?: boolean;
  program_id?: string;
};

export type TLAIUpdateDataCheckResponse = TLAISuccessResponse;

export type TLAIBulkUpdateDataChecksRequest = {
  data_check_ids: string[];
  priority?: string;
  is_global?: boolean;
  category_id?: string;
  dimension_id?: string;
  check_table?: string;
  check_field?: string;
};

export type TLAIBulkUpdateDataChecksResponse = {
  total: number;
  updated_ids: string[];
  failed_ids: string[];
};

export type TLAIUpdateDataCheckCodeRequest = {
  data_check_id: string;
  code: string;
};

export type TLAIUpdateDataCheckCodeResponse = TLAISuccessResponse;

export enum TLAIRemedyStatus {
  CREATED = 'CREATED',
  INITIAL_SCAN_IN_PROGRESS = 'INITIAL_SCAN_IN_PROGRESS',
  INITIAL_SCAN_COMPLETED = 'INITIAL_SCAN_COMPLETED',
  INITIAL_SCAN_FAILED = 'INITIAL_SCAN_FAILED',
  SETUP_COMPLETED = 'SETUP_COMPLETED',
  SETUP_IN_PROGRESS = 'SETUP_IN_PROGRESS',
  SETUP_FAILED = 'SETUP_FAILED',
}

export enum TLAITransportRequestStatus {
  MODIFIABLE = 'MODIFIABLE',
  RELEASED = 'RELEASED',
}

export enum TLAIRemedyScanStatus {
  CREATED = 'CREATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELED = 'CANCELED',
  FAILED = 'FAILED',
}

export type TLAITransportRequestSummary = {
  request_id: string; // uuid
  number: string;
  status: TLAITransportRequestStatus;
};

export type TLAITransportRequestDetail = {
  request_id: string; // uuid
  number: string;
  task_number?: string | null;
  description?: string | null;
  status: TLAITransportRequestStatus;
};

export type TLAIBulkDeleteDataChecksFromLibraryRequest = {
  library_id: string;
  data_check_ids: string[];
};

export type TLAIBulkDeleteDataChecksFromLibraryResponse = {
  total: number;
  successful_ids: string[];
  failed_ids: string[];
};

// ============== Dashboards ==============

export type TLAIDHGetAllDashboardsResponse = {
  dashboards: TLAIDataHarmonizationDashboard[];
};

export type TLAIDHCreateDashboardRequest = {
  name: string;
  description?: string;
  workstream?: string;
  target_score?: number;
  data_check_ids?: string[];
};

export type TLAIDHGetDashboardRequest = {
  dashboard_id: string;
  include_config?: boolean;
};

export type TLAIDHGetDashboardResponse = TLAIDataHarmonizationDashboard;

export type TLAIDHUpdateDashboardRequest = {
  dashboard_id: string;
  name?: string;
  description?: string;
  workstream?: string;
  target_score?: number;
};

export type TLAIDHUpdateDashboardResponse = TLAISuccessResponse;

export type TLAIDHDeleteDashboardRequest = {
  dashboard_id: string;
};

export type TLAIDHDeleteDashboardResponse = TLAISuccessResponse;

export type TLAIDHGetDashboardDataChecksRequest = {
  dashboard_id: string;
};

export type TLAIDHGetDashboardDataChecksResponse = {
  data_checks: TLAIDataHarmonizationDataCheck[];
};

export type TLAIDHDashboardDataCheckCreateType = {
  data_check_id: string;
  nickname?: string;
  priority?: TLAIDataHarmonizationDataCheckPriority;
};

export type TLAIDHAddDataCheckToDashboardRequest = {
  dashboard_id: string;
  data_checks: TLAIDHDashboardDataCheckCreateType[];
};

export type TLAIDHAddDataCheckToDashboardResponse = {
  total: number;
  successful_ids: string[];
  failed_ids: Record<string, string>;
};

export type TLAIDHGetDashboardDataCheckRecordsRequest = {
  dashboard_id: string;
};

export enum TLAIDataHarmonizationDataCheckRecordStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export type TLAIDataHarmonizationDataCheckRecord = {
  dashboard_data_check_id: string;
  dashboard_id: string;
  data_check_id: string;
  rule_id?: string;
  nickname?: string;
  priority?: TLAIDataHarmonizationDataCheckPriority;
  status?: TLAIDataHarmonizationDataCheckRecordStatus;
  created_by: string;
  updated_by: string;
  created_date: string;
  updated_date: string;
};

export type TLAIDHEnrichedDataCheckRecord = TLAIDataHarmonizationDataCheckRecord & {
  library_id: string;
  program_id: string;
  name: string;
  category?: TLAIDataHarmonizationDataCheckCategory;
  dimension?: TLAIDataHarmonizationDimension;
  check_metadata?: Record<string, any>;
};

export type TLAIDHGetDashboardDataCheckRecordsResponse = {
  dashboard_data_checks: TLAIDHEnrichedDataCheckRecord[];
};

export type TLAIDHAddDataChecksToDashboardRequest = {
  dashboard_id: string;
  data_checks: TLAIDHDashboardDataCheckCreateType[];
};

export type TLAIDHAddDataChecksToDashboardResponse = {
  total: number;
  successful_ids: string[];
  failed_ids: Record<string, string>;
};

export type TLAIDHAnalyticsFilter = {
  key: string;
  value: any;
  operator: 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte' | 'in' | 'not_in' | 'like' | 'is_null' | 'is_not_null';
};

export type AnalyticsSelectField =
  | string
  | {
      field: string;
      aggregate?: 'sum' | 'avg' | 'count' | 'min' | 'max';
      alias?: string;
      null_values?: string | number;
    };

export type TLAIDHAnalyticsQueryRequest = {
  dashboard_id: string;
  source_id?: string;
  select: AnalyticsSelectField[];
  calculations?: TLAnalyticsCalculation[];
  filters?: TLAIDHAnalyticsFilter[];
  group_by?: string[];
  order_by?: string[];
  limit?: number;
  distinct?: boolean;
  latest_per_data_check: boolean;
  partition_by_time_grain?: string;
};

export type TLAIDHAnalyticsQueryResponse = {
  columns: string[];
  data: Record<string, any>[];
  total_rows: number;
};

export type TLAIDHErrorTracking = {
  error_tracking_id: string;
  dashboard_id: string;
  program_id?: string;
  xlsx_program_id?: string;
  field_metadata: Record<string, any>[];
  status: string;
  batch_execution_id?: string | null;
  created_date: string;
  updated_date: string;
  created_by: string;
  updated_by: string;
};

export enum TLAIFilterOperator {
  EQUALS = '=',
  NOT_EQUALS = '!=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL = '<=',
  LIKE = 'LIKE',
  ILIKE = 'ILIKE',
  IN = 'IN',
  NOT_IN = 'NOT IN',
  IS_NULL = 'IS NULL',
  IS_NOT_NULL = 'IS NOT NULL',
  CONTAINS_ANY = 'contains_any',
  CONTAINS_ALL = 'contains_all',
  NOT_CONTAINS_ANY = 'not_contains_any',
  NOT_CONTAINS_ALL = 'not_contains_all',
}

export type TLAIFilter = {
  key: string;
  operator: TLAIFilterOperator;
  value: unknown;
};

export enum TLAISortDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export type TLAISort = {
  key: string;
  direction: TLAISortDirection;
};

export type TLAIDHGetErrorTrackingDataRequest = {
  error_tracking_id: string;
  page: number;
  page_size: number;
  filters: TLAIFilter[];
  sort: TLAISort[];
};

export type TLAIDHGetErrorTrackingDataResponse = {
  tsv_data: string;
  field_metadata: Record<string, any>[];
  last_refreshed: string;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  filters: TLAIFilter[];
};

export type TLAIDHGetAllErrorTrackingRequest = {
  dashboard_id: string;
};

export type TLAIDHGetAllErrorTrackingResponse = {
  error_tracking_results: TLAIDHErrorTracking[];
};

export type TLAIDHRefreshErrorTrackingRequest = {
  dashboard_id: string;
};

export type TLAIDHRefreshErrorTrackingResponse = {
  error_tracking: TLAIDHErrorTracking;
};

export type TLAIDHRecalculateErrorTrackingRequest = {
  error_tracking_id: string;
  dashboard_id: string;
};

export type TLAIDHDeleteErrorTrackingRequest = {
  error_tracking_ids: string[];
  dashboard_id: string;
};

// ============== Dashboard Data Check ==============

export type TLAIDHUpdateDashboardDataCheckRequest = {
  dashboard_data_check_id: string;
  nickname?: string;
  comments?: string;
  priority?: TLAIDataHarmonizationDataCheckPriority;
  status?: TLAIDataHarmonizationDataCheckRecordStatus;
};

export type TLAIDHUpdateDashboardDataCheckResponse = {
  dashboard_data_check: TLAIDataHarmonizationDataCheckRecord;
};

export type TLAIDHGetDashboardDataCheckParametersRequest = {
  dashboard_data_check_id: string;
  sap_version: TLSAPVersion;
};

export type TLAIDHGetDashboardDataCheckParametersResponse = {
  parameters: TLAISAPParameter[];
};

export type TLAIDHSaveDashboardDataCheckParametersRequest = {
  dashboard_data_check_id: string;
  parameters: TLAISAPParameter[];
};

export type TLAIDHSaveDashboardDataCheckParametersResponse = {
  dashboard_data_check: TLAIDataHarmonizationDataCheckRecord;
};

export type TLAIDHGetDashboardDataCheckExecutionsRequest = {
  dashboard_data_check_id: string;
  limit: number;
};

export type TLAIDHGetDashboardDataCheckExecutionsResponse = {
  executions: TLAIDHDataCheckExecution[];
};

// ============== Data Check Execution ==============

export enum TLAIDataCheckExecutionStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  SAVING = 'SAVING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

export type TLAIDHExecutionResult = {
  issue_count?: number;
  total_scanned?: number;
  error?: string;
  canceled_by?: string;
  message?: string;
  field_metadata?: Record<string, any>[];
  [key: string]: any;
};

export type TLAIDHStorageBackend = 'LAKE' | 'PROGRAM';

export type TLAIDHDataCheckExecution = {
  execution_id: string;
  data_check_id: string;
  dashboard_id?: string;
  dashboard_data_check_id?: string;
  status: TLAIDataCheckExecutionStatus;
  result?: TLAIDHExecutionResult;
  result_program_id?: string;
  xlsx_program_id?: string;
  storage?: TLAIDHStorageBackend | null;
  parameters?: Record<string, any>;
  execution_metadata?: Record<string, any>;
  started_date?: string;
  completed_date?: string;
  created_by: string;
  created_date: string;
  updated_date: string;
  executionStatus?: TLAIDataCheckExecutionStatus;
  score?: number;
  duration_ms?: number;
};

export type TLAIDHGetDashboardExecutionsRequest = {
  dashboard_id: string;
  limit: number;
};

export type TLAIDHGetDashboardExecutionsResponse = {
  executions: TLAIDHDataCheckExecution[];
};

export type TLAIDHCancelExecutionsRequest = {
  execution_ids: string[];
};

export type TLAIDHCancelExecutionsResponse = {
  executions: TLAIDHDataCheckExecution[];
};

export type TLAIDHDeleteExecutionRequest = {
  execution_ids: string[];
};

export type TLAIDHDeleteExecutionResponse = {
  delete_count: number;
};

export type TLAIDHGetExecutionResultRequest = {
  execution_id: string;
  page: number;
  page_size: number;
  filters?: TLAIFilter[];
  sort?: TLAISort[];
};

export type TLAIDHGetExecutionResultResponse = {
  tsv_data: string;
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  filters: TLAIFilter[];
};

export type TLAIDHExportExecutionResultRequest = {
  execution_id: string;
  filters?: TLAIFilter[];
  sort?: TLAISort[];
};

export type TLAIDHExportExecutionResultResponse = {
  file?: TLAIFile | null;
  notify_on_completion?: boolean;
};

export type TLAIDHListAllExecutionsRequest = {
  limit: number;
  offset: number;
  status?: TLAIDataCheckExecutionStatus[];
  dashboard_id?: string[];
};

export type TLAIDHListAllExecutionsResponse = {
  executions: TLAIDHDataCheckExecution[];
  total_count: number;
  limit: number;
  offset: number;
};

// ============== Storage Status ==============

export type TLAIDHExecutionStorageStatus = {
  execution_id: string;
  data_check_id: string;
  batch_execution_id?: string | null;
  storage?: TLAIDHStorageBackend | null;
  result_table_fqdn?: string | null;
  readable?: boolean | null;
  checked_at?: string | null;
  completed_date?: string | null;
};

export type TLAIDHDataCheckStorageStatus = {
  data_check_id: string;
  executions: TLAIDHExecutionStorageStatus[];
};

/** Lake-vs-program storage split for one result type. Top-level counts are the sum of all three. */
export type TLAIDHStorageCounts = {
  total_executions: number;
  lake_count: number;
  program_count: number;
  unreadable_count: number;
};

export type TLAIDHStorageStatus = {
  dashboard_id: string;
  dashboard_name?: string | null;
  total_executions: number;
  lake_count: number;
  program_count: number;
  unreadable_count: number;
  data_checks?: TLAIDHStorageCounts;
  error_tracking?: TLAIDHStorageCounts;
  custom_views?: TLAIDHStorageCounts;
  checks: TLAIDHDataCheckStorageStatus[];
};

export type TLAIDHTenantStorageStatus = {
  total_executions: number;
  lake_count: number;
  program_count: number;
  unreadable_count: number;
  data_checks?: TLAIDHStorageCounts;
  error_tracking?: TLAIDHStorageCounts;
  custom_views?: TLAIDHStorageCounts;
  /** Effective per-tenant Data Foundry storage flag — drives the admin-console migration toggle. */
  foundry_storage_enabled: boolean;
  dashboards: TLAIDHStorageStatus[];
};

export type TLAIDHStorageModeRequest = {
  enabled: boolean;
};

export type TLAIDHStorageModeResponse = {
  foundry_storage_enabled: boolean;
};

export type TLAIDHStorageBackfillResponse = {
  dashboard_id: string;
  candidate_count: number;
  message: string;
};

// ============== Error Tracking ==============

export type TLAIDHExportErrorTrackingDataRequest = {
  error_tracking_id: string;
  filters?: TLAIFilter[];
  sort?: TLAISort[];
};

export type TLAIDHExportErrorTrackingDataResponse = {
  file: TLAIFile;
};

// ============== Remediation ==============

export type TLAIRemedy = {
  remedy_id: string; // uuid
  name: string;
  check_variant: string;
  sap_system_version: string;
  status: TLAIRemedyStatus;
  created_by: string;
  updated_by: string;
  created_date: string; // ISO
  updated_date: string; // ISO
  transport_request?: TLAITransportRequestSummary; // Optional for backward compatibility
  // The Code Modernization project this remedy backs, or null/absent for a legacy remedy and for
  // one whose project was deleted — the backend resolves the link through the ACTIVE project, so
  // a value here always names a project that still exists (MOS-2482).
  project_id?: string | null;
};

export type TLAIRemedyListResponse = {
  remedies: TLAIRemedy[];
};

export type TLAIRemedyObjectResponse = {
  object_id: string; // uuid
  parent_id?: string | null;
  name: string;
  object_type: string;
  category?: string | null;
  program_id?: string | null;
};

export enum TLAIRemedyObjectStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  FLAGGED_FOR_FUNCTIONAL = 'FLAGGED_FOR_FUNCTIONAL',
  READY_FOR_REVIEW = 'READY_FOR_REVIEW',
  COMPLETED = 'COMPLETED',
}

export type TLAIRemedyObjectDetail = {
  object_id: string; // uuid
  parent_id?: string | null;
  name: string;
  object_type: string;
  category?: string | null;
  program_id?: string | null;
  tr_number?: string | null;
  task_number?: string | null;
  tr_description?: string | null;
  status?: TLAIRemedyObjectStatus;
  assigned_to?: string[];
};

export type TLAIScanInfoResponse = {
  scan_id: string; // uuid
  object_id: string; // uuid
  status: TLAIRemedyScanStatus;
  request_id: string; // uuid
  created_date: string; // ISO format
  updated_date: string; // ISO format
};

export type TLAIRemedyObjectsListResponse = {
  objects: TLAIRemedyObjectResponse[];
  scans: TLAIScanInfoResponse[];
};

export type TLAIRemedyRelatedObjectsRequest = {
  remedy_id: string;
  object_id: string;
};

export type TLAIRemedyDetailResponse = {
  remedy_id: string; // uuid
  name: string;
  status: TLAIRemedyStatus;
  sap_system_version: TLSAPVersion;
  objects: TLAIRemedyObjectDetail[];
  transport_request?: TLAITransportRequestDetail | null; // Optional for backward compatibility
};

export type TLAIRemedyDetailRequest = {
  remedy_id: string;
};

export type TLAIRemedyUpdateRequest = {
  remedy_id: string;
  name: string;
};

export type TLAIRemedyDeleteRequest = {
  remedy_id: string;
};

export type TLAIFindingResponse = {
  finding_id: string; // uuid
  scan_id: string; // uuid
  object_id: string; // uuid
  check_title: string;
  check_message: string;
  priority: string;
  line?: number | null;
  sap_note_number?: string | null;
  package?: string | null;
};

export type TLAIFindingsListResponse = {
  findings: TLAIFindingResponse[];
};

export type TLAIFindingsForScanObjectRequest = {
  scan_id: string;
  object_id: string;
};

export type TLAIRemedyObjectInput = {
  name: string;
  object_type: string;
  category: string;
};

export type TLAIRemedyCreateRequest = {
  name: string;
  check_variant: string;
  sap_version: TLSAPVersion;
  objects: TLAIRemedyObjectInput[];
  description?: string | null;
  tr_number?: string | null;
};

export type TLAIRemedyCreateResponse = {
  remedy_id: string; // uuid
  name: string;
  status: TLAIRemedyStatus;
};

export type TLAIRescanResponse = {
  scan_ids: string[];
};

export type TLAIRescanObjectRequest = {
  remedy_id: string;
  parent_id: string;
};

export type TLAIIssueFixResponse = {
  issue_id: string; // finding_id
  fix_id: string; // fix_library_id
  confidence_score: number;
  description: string;
  fix_title: string;
};

export type TLAIIssueFixesListResponse = {
  fixes: TLAIIssueFixResponse[];
};

export type TLAIIssueWithFixes = {
  issue_id: string; // finding_id
  fix_ids: string[]; // fix_library_ids
};

export type TLAIResolveIssuesRequest = {
  object_id: string;
  issues: TLAIIssueWithFixes[];
};

export type TLAIResolveIssuesResponse = {
  program_id: string; // uuid
  resolved_code: string;
  analysis: string;
};

export type TLAISapUpdateRequest = {
  remedy_id: string; // uuid
  parent_id: string; // uuid
  sap_version: TLSAPVersion;
};

export type TLAISapUpdateResult = {
  object_id: string; // uuid
  object_name: string;
  program_id?: string | null; // uuid
  success: boolean;
  error_message?: string | null;
  program_version?: string | null;
};

export type TLAISapUpdateResponse = {
  parent_object_id: string; // uuid
  parent_object_name: string;
  total_objects: number;
  successful_updates: number;
  failed_updates: number;
  results: TLAISapUpdateResult[];
};

export type TLAIObjectSyntaxCheckRequest = {
  parent_id: string; // uuid
  sap_version?: TLSAPVersion; // Matches OpenAPI spec
};

export enum TLAIObjectSyntaxCheckStatus {
  OK = 'ok',
  FAIL = 'fail',
  ERROR = 'error',
  UNKNOWN = 'unknown',
}

export type TLAIObjectSyntaxCheckResult = {
  object_id: string; // uuid
  object_name: string;
  syntax_status: TLAIObjectSyntaxCheckStatus;
  message?: string | null;
  line?: number | null;
  word?: string | null;
};

export type TLAIObjectSyntaxCheckResponse = {
  results: TLAIObjectSyntaxCheckResult[];
};

export type TLAIStartRemedyRequest = {
  name: string;
  check_variant: string;
  program_id: string; // uuid
  sap_version?: TLSAPVersion;
};

export type TLAIStartRemedyResponse = {
  remedy_id: string;
  name: string;
  status: TLAIRemedyStatus;
};

export type TLAIObjectAssignmentItem = {
  object_id: string; // uuid
  assigned_email: string;
};

export type TLAIObjectAssignmentRequest = {
  remedy_id: string;
  assignments: TLAIObjectAssignmentItem[];
};

export type TLAIObjectAssignmentResponse = {
  message?: string | null;
  failed_object_ids?: string[] | null; // uuid[]
};

export type TLAISourceCodeRequest = {
  remedy_id: string;
  object_id: string; // uuid
  /** Which S/4 slot to read: the editable working copy (default) or the live active version. */
  version?: 'active' | 'inactive';
};

export type TLAISourceCodeResponse = {
  object: TLAIRemedyV3ObjectDetail;
  program: TLAIProgram;
};

// ============== Code Remediation v3 Types ==============

export enum TLAIRemedyV3Priority {
  HIGH = '1',
  MEDIUM = '2',
  LOW = '3',
}

export enum TLAIRemedyV3FindingStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  FLAGGED_FOR_FUNCTIONAL = 'FLAGGED_FOR_FUNCTIONAL',
  BUSINESS_APPROVED_TO_IGNORE = 'BUSINESS_APPROVED_TO_IGNORE',
  ON_HOLD = 'ON_HOLD',
  MANUAL_CHANGE_NEEDED = 'MANUAL_CHANGE_NEEDED',
}

export type TLAIRemedyV3FindingResponse = {
  finding_id: string; // uuid
  object_id: string; // uuid
  check_title: string;
  check_message: string;
  priority: TLAIRemedyV3Priority;
  line?: number | null;
  sap_note_number?: string | null;
  subobject?: string | null;
  status: TLAIRemedyV3FindingStatus;
  additional_info?: string | null;
  pseudocomment?: string | null;
};

export type TLAIRemedyV3FindingsListResponse = {
  findings: TLAIRemedyV3FindingResponse[];
};

export type TLAIRemedyV3Response = {
  remedy_id: string; // uuid
  name: string;
  check_variant: string;
  sap_system_version: string;
  status: TLAIRemedyStatus;
  created_by: string;
  updated_by: string;
  created_date: string; // ISO date-time
  updated_date: string; // ISO date-time
};

export type TLAIRemedyV3ListResponse = {
  remedies: TLAIRemedyV3Response[];
};

export type TLAIRemedyV3ObjectDetail = {
  object_id: string; // uuid
  parent_id?: string | null; // uuid
  name: string;
  object_type: string;
  adt_resource_path?: string | null;
  category?: string | null;
  program_id?: string | null; // uuid
  tr_number?: string | null;
  task_number?: string | null;
  tr_description?: string | null;
  status?: TLAIRemedyObjectStatus | null;
  assigned_to?: string | null;
  assigned_by?: string | null;
  subcategory?: string | null;
  sap_program_name?: string | null;
};

export type TLAIRemedyV3DetailResponse = {
  remedy_id: string; // uuid
  name: string;
  status: TLAIRemedyStatus;
  sap_system_version: TLSAPVersion;
  objects: TLAIRemedyV3ObjectDetail[];
  /**
   * The Code Modernization project this remedy backs, if any (MOS-2482).
   *
   * Null for a remedy created directly in Code Remediation, and for one whose owning project was
   * deleted — the backend resolves it against live projects, so it is never an id that 404s.
   * Mirrors `TLRemedyDetailResponse.project_id` in the generated client; this hand-written type
   * is what `useRemedyV3DetailQuery` returns.
   */
  project_id?: string | null; // uuid
};

export type TLAIRemedyV3CreateResponse = {
  remedy_id: string; // uuid
  name: string;
  status: TLAIRemedyStatus;
};

export type TLAIObjectDetailsRequest = {
  name: string;
  check_variant: string;
  program_id: string; // uuid
  sap_version?: TLSAPVersion;
};

export type TLAIPackageImportPreviewRequest = {
  package_name: string;
  /** Inclusive range end; with it set, package_name is the range start. */
  package_name_end?: string;
  sap_version?: TLSAPVersion;
};

export type TLAIPackageImportObject = {
  name: string;
  object_type: string;
  subobject_count: number;
};

export type TLAIPackageImportPreviewResponse = {
  objects: TLAIPackageImportObject[];
  total_count: number;
  page_size: number;
  offset: number;
  package_name: string;
  package_name_end?: string | null;
};

export type TLAIPackageImportObjectRef = {
  name: string;
  object_type: string;
};

export type TLAIPackageImportCreateRequest = {
  name: string;
  check_variant: string;
  sap_version?: TLSAPVersion;
  package_name: string;
  /** Inclusive range end; with it set, package_name is the range start. */
  package_name_end?: string;
  deselected_objects?: TLAIPackageImportObjectRef[];
};

export type TLAITransportImportPreviewRequest = {
  transport_numbers: string[];
  sap_version?: TLSAPVersion;
};

export type TLAITransportImportObject = {
  name: string;
  object_type: string;
  source_transport: string;
  subobject_count: number;
};

export type TLAITransportImportPreviewResponse = {
  objects: TLAITransportImportObject[];
  total_count: number;
  page_size: number;
  offset: number;
  transport_numbers: string[];
};

export type TLAITransportImportObjectRef = {
  name: string;
  object_type: string;
};

export type TLAITransportImportCreateRequest = {
  name: string;
  check_variant: string;
  sap_version?: TLSAPVersion;
  transport_numbers: string[];
  deselected_objects?: TLAITransportImportObjectRef[];
};

/** Mirrors the backend's `SearchableObjectType` — the subset of object types the SAP search supports. */
export enum TLAISearchableObjectType {
  CLAS = 'CLAS',
  FUGR = 'FUGR',
  INTF = 'INTF',
  PROG = 'PROG',
}

export type TLAISapObjectSearchRequest = {
  object_type: TLAISearchableObjectType;
  name_pattern: string;
  /** Inclusive range end; with it set, name_pattern is the literal range start, not a glob. */
  name_end?: string;
  sap_version?: TLSAPVersion;
};

export type TLAISapObjectSearchResult = {
  name: string;
  object_type: string;
  description?: string | null;
  package_name?: string | null;
  uri: string;
};

export type TLAISapObjectSearchResponse = {
  objects: TLAISapObjectSearchResult[];
  total_count: number;
  page_size: number;
  offset: number;
  object_type: string;
  name_pattern: string;
  name_end?: string | null;
};

export type TLAISapObjectImportCreateRequest = {
  name: string;
  check_variant: string;
  sap_version?: TLSAPVersion;
  objects: TLAIPackageImportObjectRef[];
};

export type TLAIScanRequest = {
  object_ids: string[]; // uuid[]
};

export type TLAIScanResponse = {
  scan_ids: string[]; // uuid[]
};

export type TLAIUpdateSourceCodeRequest = {
  remedy_id: string; // uuid
  object_id: string; // uuid
  parent_id?: string | null; // uuid
  code: string;
  /**
   * The transport request to use when this object is on none and its project has no default one —
   * the answer to a 428 from this endpoint. The backend records it as the project's default, so
   * later objects in the project are not asked. Ignored when the object or the project already has
   * one: an object's existing transport is never replaced.
   */
  tr_number?: string;
};

export type TLAIUpdateSourceCodeResponse = {
  success: boolean;
  message: string;
  /** Inactive source read back from SAP after the write; absent when the read-back failed */
  code?: string | null;
};

export type TLAIRemedyV3ObjectInfo = {
  object_id: string; // uuid
  parent_id?: string | null; // uuid
  name: string;
  object_type: string;
  category?: string | null;
  program_id?: string | null; // uuid
  tr_number?: string | null;
  tr_description?: string | null;
  status?: TLAIRemedyObjectStatus | null;
  assigned_to?: string | null;
  assigned_by?: string | null;
  subcategory?: string | null;
};

export type TLAIRemedyV3ScanInfo = {
  scan_id: string; // uuid
  object_id: string; // uuid
  status: TLAIRemedyScanStatus;
  created_date: string; // ISO format
  updated_date: string; // ISO format
};

export type TLAIRemedyV3ObjectsListResponse = {
  objects: TLAIRemedyV3ObjectInfo[];
  scans: TLAIRemedyV3ScanInfo[];
};

export enum TLAIMessageType {
  ERROR = 'E',
  WARNING = 'W',
  INFO = 'I',
  UNKNOWN = 'U',
}

export type TLAICheckMessage = {
  file: string;
  line: number;
  column: number;
  text: string;
  type: TLAIMessageType;
};

export type TLAIRemedyV3SyntaxCheckRequest = {
  remedy_id: string; // uuid
  object_id: string; // uuid
  code?: string | null;
};

export type TLAIRemedyV3SyntaxCheckResponse = {
  success: boolean;
  messages: TLAICheckMessage[];
};

export type TLAIActivationRequest = {
  remedy_id: string; // uuid
  object_ids: string[]; // uuid[]
};

// Serialized under SAP's own ADT field names (objDescr/shortText) because the backend model
// aliases them; sourced from codegen so a hand-written copy can't drift off them again.
export type TLAIActivationMessage = TLCodeRemediationV3ActivationMessage;

export type TLAIActivationResponseObject = {
  object_id: string; // uuid
  success: boolean;
  error_messages?: TLAIActivationMessage[];
};

export type TLAIActivationResponse = {
  results: TLAIActivationResponseObject[];
};

export type TLAIRemedyV3ScanRequest = {
  remedy_id: string; // uuid
  object_ids: string[]; // uuid[]
};

export type TLAIRemedyV3CancelScansRequest = {
  scan_ids: string[]; // uuid[]
};

export type TLAIRemedyV3UpdateObjectStatusRequest = {
  remedy_id: string; // uuid
  object_id: string; // uuid
  status: TLAIRemedyObjectStatus;
};

export type TLAIRemedyV3UpdateObjectStatusResponse = {
  message: string;
  status: 'success';
  syntax_messages?: TLAICheckMessage[] | null;
};

export type TLAIRemedyV3UpdateFindingStatusRequest = {
  object_id: string; // uuid
  scan_id: string; // uuid
  finding_id: string; // uuid
  status: TLAIRemedyV3FindingStatus;
};

export type TLAIRemedyV3UpdateFindingStatusResponse = {
  message: string;
  status: 'success';
};

export type TLAIRemedyV3ModLogRequest = {
  remedy_id: string; // uuid
  object_id: string; // uuid
  parent_id: string; // uuid
};

export type TLAIRemedyV3ModLogResponse = {
  mod_log_comment: string;
};

export type TLAINextLintAbapRequest = {
  session_id?: string;
  filename?: string;
  type?: string;
  code: string;
};

export type TLAINextFixAbapRequest = {
  session_id?: string;
  filename: string;
  type?: string;
  code: string;
  variant?: 'all' | 'general-only';
};

export type TLAINextAbapContextRequest = {
  session_id: string;
  code: string;
  filename: string;
  type: string;
};

export type TLAINextAbapContextResponse = {
  success: boolean;
};

export type TLAITextEdit = {
  to: number;
  from: number;
  replacement: string;
};

export type TLAICodeFix = {
  key: string;
  description: string;
  edits: TLAITextEdit[];
};

export type TLAIEditorDiagnostic = Omit<BaseDiagnostic, 'actions'> &
  DiagnosticExtension & {
    fixes?: TLAICodeFix[];
  };

export type TLAINextLintAbapResponse = {
  diagnostics: TLAIEditorDiagnostic[];
};

export type TLAINextFixAbapResponse = {
  diagnostics: TLAIEditorDiagnostic[];
};

export type TLAINextObjectIssue = {
  object_id: string;
  errors?: string[] | null;
  issue_keys: string[];
};

export type TLAINextFindObjectsWithIssuesRequest = {
  session_id?: string;
  remedy_id: string;
  object_ids: string[];
  max_object_ids_per_request?: number;
};

export type TLAINextFindObjectsWithIssuesResponse = {
  items: TLAINextObjectIssue[];
};

export type TLAINextFixObjectsWithIssuesRequest = {
  remedy_id: string;
  object_ids: string[];
  issue_keys: string[];
  max_object_ids_per_request?: number;
  skipCommentingRules?: string[];
  skip_fle_remediation?: boolean;
  format_on_save?: boolean;
  sap_version?: string;
};

export type TLAINextFixObjectsWithIssuesItem = {
  object_id: string;
  success: boolean;
  error?: string | null;
};

export type TLAINextFixObjectWithIssuesRequest = {
  remedy_id: string;
  object_id: string;
  code: string;
  issue_keys: string[];
  skipCommentingRules?: string[];
};

export type TLAINextFixObjectWithIssuesResponse = {
  code: string;
  hasAppliedFixes: boolean;
  error?: string;
  success: boolean;
};

export type TLAIAbapStaticFix = {
  key: string;
  description: string;
  title?: string;
  tags?: string[];
  metadata?: Partial<AbapLintRuleMeta>;
};

export type TLAIAbapStaticFixListResponse = {
  staticFixes: TLAIAbapStaticFix[];
};

export type TLAINextFixObjectsWithIssuesResponse = {
  items: TLAINextFixObjectsWithIssuesItem[];
};

export type TLAISelectTenantRequest = {
  tenant_id?: string;
};
